export interface PdfTextItem {
  str: string;
  transform: number[];
  hasEOL?: boolean;
}

export interface PdfLine {
  y: number;
  items: PdfTextItem[];
}

const shouldInsertSpaceBetweenItems = (current: PdfTextItem, next: PdfTextItem): boolean => {
  const charWidth = current.transform[0] || 1;
  const textWidth = current.str.length * charWidth;
  const currentXEnd = current.transform[4] + textWidth;
  const nextXStart = next.transform[4];
  const spaceBetween = nextXStart - currentXEnd;
  return spaceBetween > 1;
};

const calculateAverageLineHeight = (lines: PdfLine[]): number => {
  if (lines.length < 2) {
    return 0;
  }

  const lineHeights: number[] = [];

  for (let i = 0; i < lines.length - 1; i++) {
    const height = Math.abs(lines[i].y - lines[i + 1].y);
    lineHeights.push(height);
  }

  if (lineHeights.length === 0) {
    return 0;
  }

  const total = lineHeights.reduce((sum, value) => sum + value, 0);
  return total / lineHeights.length;
};

export const mergePdfLines = (rawLines: PdfLine[]): string => {
  if (rawLines.length === 0) {
    return '';
  }

  const lines = [...rawLines].sort((a, b) => b.y - a.y);
  const avgLineHeight = calculateAverageLineHeight(lines);

  let pageText = '';
  let lastY = -1;
  let previousLineEndedWithHyphen = false;
  let previousLineHadExplicitBreak = false;

  for (const line of lines) {
    let lineText = '';

    for (let j = 0; j < line.items.length; j++) {
      const item = line.items[j];
      lineText += item.str;

      if (j < line.items.length - 1) {
        const currentItem = line.items[j];
        const nextItem = line.items[j + 1];
        if (shouldInsertSpaceBetweenItems(currentItem, nextItem)) {
          lineText += ' ';
        }
      }
    }

    const trimmedLineText = lineText.trim();
    if (trimmedLineText.length === 0) {
      continue;
    }

    const gap = lastY === -1 ? 0 : Math.abs(line.y - lastY);
    const currentLineHasExplicitBreak = line.items.some((item) => Boolean(item.hasEOL));
    const isParagraphBreak = lastY !== -1 && avgLineHeight > 0 && gap > avgLineHeight * 1.5;
    const isWordWrap = lastY !== -1 && avgLineHeight > 0 && gap >= avgLineHeight * 0.7 && gap <= avgLineHeight * 1.3;
    const isLineBreak = lastY !== -1 && avgLineHeight > 0 && gap > avgLineHeight * 1.3 && gap <= avgLineHeight * 1.5;

    let separator: string | null = null;

    if (lastY !== -1) {
      if (previousLineEndedWithHyphen && isWordWrap) {
        if (pageText.length > 0 && pageText.endsWith('-')) {
          pageText = pageText.slice(0, -1);
        }
        separator = '';
      } else if (isParagraphBreak || (previousLineHadExplicitBreak && gap > avgLineHeight * 0.8)) {
        separator = '\n\n';
      } else if (previousLineHadExplicitBreak || isLineBreak) {
        separator = '\n';
      } else if (isWordWrap) {
        separator = ' ';
      } else {
        separator = ' ';
      }
    }

    if (separator) {
      pageText += separator;
    }

    pageText += trimmedLineText;
    lastY = line.y;
    previousLineEndedWithHyphen = trimmedLineText.endsWith('-');
    previousLineHadExplicitBreak = currentLineHasExplicitBreak;
  }

  return pageText;
};

